import {
  EstimatedTimeOfArrival,
  fetchEstimatedTimeOfArrival,
} from 'api/product';
import IconCLock from 'assets/icons/clock.svg';
import { useConfig } from 'config';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useSWR from 'swr';

interface Props {
  variantId: string;
}

const ONE_WEEK = 7;
const FOUR_WEEKS = 28;
const ONE_MONTH = 30;
const THREE_MONTHS = 90;

const DAYS = 'days';
const WEEKS = 'weeks';
const MONTHS = 'months';
const UNKNOWN = 'unknown';

/**
 * Component that displays the ETA of the product (on variant level), when it is not available for sale.
 */
const EtaCommunication: React.FC<Props> = ({ variantId }) => {
  const { formatDate } = useIntl();
  const { oosEndpoint } = useConfig();

  const { data, error } = useSWR<EstimatedTimeOfArrival | null | undefined>(
    `ETA-${variantId}`,
    () => fetchEstimatedTimeOfArrival(oosEndpoint, [Number(variantId)])
  );

  const eta = useMemo(() => {
    if (!data) return null;
    return data[variantId]?.eta;
  }, [data, variantId]);

  const calculatedEta = useMemo(() => {
    if (!eta) return { parsedEta: '', messageId: UNKNOWN };
    const [day, month, year] = eta.split('/');
    let messageId: string;
    let parsedEta: string;

    const today = new Date();
    const etaDate = new Date(Number(year), Number(month) - 1, Number(day));
    const diffInDays = Math.ceil(
      (etaDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (isNaN(diffInDays)) {
      messageId = UNKNOWN;
      parsedEta = '';
    } else if (diffInDays <= ONE_WEEK) {
      messageId = DAYS;
      parsedEta = formatDate(etaDate, {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
      });
    } else if (diffInDays <= FOUR_WEEKS) {
      const weeks = Math.ceil(diffInDays / ONE_WEEK);
      messageId = WEEKS;
      parsedEta = `${weeks}`;
    } else if (diffInDays <= THREE_MONTHS) {
      const months = Math.ceil(diffInDays / ONE_MONTH);
      messageId = MONTHS;
      parsedEta = `${months}`;
    } else {
      messageId = UNKNOWN;
      parsedEta = '';
    }

    return { parsedEta, messageId };
  }, [eta, formatDate]);

  if (!eta || error) return null;

  return (
    <div className="bg-grey-50 flex items-center gap-1 rounded-sm p-1.5">
      <IconCLock className="h-4 w-4 shrink-0 text-orange-500" />
      <span className="text-text-secondary text-sm">
        <FormattedMessage
          id={`pdp:buy-panel:eta-communication:${calculatedEta.messageId}`}
          values={{
            eta: (
              <span className="text-orange-500">{calculatedEta.parsedEta}</span>
            ),
          }}
        />
      </span>
    </div>
  );
};

export default EtaCommunication;
